import { METRICS } from "../constants/data"
import {
  FormState,
  RuleBlock,
  ConditionAtom,
  ConditionGroup,
  MetricKey,
} from "../types"

export const uid = () => Math.random().toString(36).slice(2, 9)

export function metricByKey(k: MetricKey) {
  return METRICS.find((m) => m.key === k)
}

export function formatAtom(atom: ConditionAtom): string {
  const m = metricByKey(atom.metric)

  // Определяем нужно ли показывать единицы измерения
  const shouldShowUnits = () => {
    // Метрики без единиц измерения (количественные)
    const noUnitsMetrics = [
      "users_count",
      "ads_clicks_count",
      "ads_impressions",
      "web_users_count",
    ]

    return !noUnitsMetrics.includes(atom.metric)
  }

  // Определяем единицу измерения по умолчанию для метрики
  const getDefaultUnit = () => {
    // Метрики в процентах
    const percentMetrics = [
      "new_users_part",
      "ctr",
      "ads_click_to_reg_converse",
      "romi",
      "drr",
      "user_to_web_user_converse",
      "user_to_buyer_converse",
      "web_5min_users_part",
      "web_15min_users_part",
      "web_60min_g_users_part",
      "web_120min_g_users_part",
    ]

    if (percentMetrics.includes(atom.metric)) {
      return "percent"
    }

    // Все остальные метрики в рублях (если показываем единицы)
    return "rub"
  }

  const displayUnit = shouldShowUnits()
    ? getDefaultUnit() === "percent"
      ? "%"
      : "руб."
    : ""

  const val = atom.value === "" ? "—" : String(atom.value)
  const daysFrom = atom.periodDays ?? 0
  const daysUntil = atom.periodDaysUntil ?? 0

  let days = ""
  if (daysFrom > 0) {
    if (daysUntil === 0) {
      // Режим "последние"
      const dayWord = getDayDeclension(daysFrom)
      days = `За последние ${daysFrom} ${dayWord}: `
    } else {
      // Режим "период"
      days = `За период с ${daysFrom} по ${daysUntil} дней назад: `
    }
  }

  const comparatorLabels: Record<string, string> = {
    ">": "больше",
    "<": "меньше",
    decreased: "уменьшилось",
    increased: "увеличилось",
  }

  const comparatorText = comparatorLabels[atom.comparator] || atom.comparator
  return `${days}${m?.label ?? atom.metric} ${comparatorText} ${val}${
    displayUnit ? ` ${displayUnit}` : ""
  }`
}

function getDayDeclension(days: number): string {
  const lastDigit = days % 10
  const lastTwoDigits = days % 100

  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
    return "дней"
  }

  if (lastDigit === 1) {
    return "день"
  }

  if (lastDigit >= 2 && lastDigit <= 4) {
    return "дня"
  }

  return "дней"
}

export function summarizeGroup(g: ConditionGroup): string {
  return g.atoms?.length
    ? g.atoms.map(formatAtom).join(" ИЛИ ")
    : "нет показателей"
}

export function summarizeBlock(b: RuleBlock): string {
  if (!b.groups?.length) return "нет групп"
  return b.groups.map((g) => `(${summarizeGroup(g)})`).join(" И ")
}

export function scopeNoneLabel(scopeOne: "campaigns" | "adGroups" | "ads") {
  return "Ничего не меняем"
}

export function toJSON(state: FormState) {
  return {
    ad_source: state.adSource,
    accounts: state.accounts,
    scope: state.scope,
    campaign_ids: state.campaignIds,
    ad_group_ids: state.adGroupIds,
    ad_ids: state.adIds,
    windows: {
      lookback_days: state.lookbackDays,
      check_every_days: state.checkEveryDays,
    },
    rules: state.rules.map((rb) => ({
      id: rb.id,
      name: rb.name,
      period_days: rb.periodDays,
      groups: rb.groups.map((g) => ({
        id: g.id,
        logic: "OR",
        period_days: g.periodDays ?? rb.periodDays,
        atoms: g.atoms.map((a) => ({
          id: a.id,
          metric: a.metric,
          comparator: a.comparator,
          value: a.value === "" ? null : Number(a.value),
          period_days: a.periodDays ?? null,
          period_days_until: a.periodDaysUntil ?? null,
        })),
      })),
      actions: {
        budgetAction: rb.actions.budgetAction,
        budgetAmount: rb.actions.budgetAmount,
        budgetUnit: rb.actions.budgetUnit,
        sendTelegram: rb.actions.sendTelegram,
        tgMessage: rb.actions.tgMessage,
        tgRecipients: rb.actions.tgRecipients.map((u) => ({
          id: u.id,
          name: u.name,
          username: u.username,
        })),
      },
    })),
  }
}
